/**
 * The growth stages on the profile's Labels screen: rename, recolour, hide,
 * reorder, and add or delete the gardener's own. A refused change is said
 * under the list; a refused rename also puts the saved name back in its box.
 *
 * Used by: LabelsScreen (Profile > Labels), under the automatic stages switch.
 * Every change refetches the stages and the plants, since cards and pickers
 * paint themselves from the stage list's colours and names.
 *
 * @module StageSettings
 */

import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Eye, EyeOff, X, GripVertical } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { selectableStages } from '@/shared/lib/stages';
import { Button } from '@/shared/components/ui/button';
import { Pill } from '@/shared/components/ui/Pill';
import { Input } from '@/shared/components/ui/input';
import { themeForColorKey, isColorKey, type ColorKey } from '@/shared/theme/colorPalette';
import { getStages, createStage, updateStage, deleteStage, reorderStages } from '@/shared/api/stages';
import { useDragReorder } from '@/shared/hooks/useDragReorder';
import { ColorPicker } from './ColorPicker';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { SavedTick } from './profileKit';
import { useSavedTicks } from './useSavedTicks';
import { useGrowLimits, useGrowSettings } from '@/shared/lib/growSettings';
import { PLANTS_QUERY_KEY, STAGES_QUERY_KEY } from '@/config/tunables/ui';

/**
 * The growth stages, and the gardener's control over them.
 *
 * Two kinds sit in one list. The ones the app shipped with can be renamed,
 * recoloured and hidden, but not removed: the app's stage-based rules (Grow's
 * automatic stages, Today's planting jobs) find them by their catalogue type
 * (never their id since batch 10b), and the backend refuses to delete one
 * (STAGE_NOT_YOURS). Stages a gardener adds are labels only, and theirs to
 * delete.
 */
export const StageSettings: React.FC = () => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const limits = useGrowLimits();
  const settings = useGrowSettings();
  const queryClient = useQueryClient();

  const [newName, setNewName] = React.useState('');
  // The colour the gardener picked for the new item; until they pick, the Grow setting's (the server's), else a neutral gray before it has been heard.
  const [pickedColor, setNewColor] = React.useState<ColorKey | null>(null);
  const settingColor = isColorKey(settings['plants.NEW_STAGE_COLOR']) ? (settings['plants.NEW_STAGE_COLOR'] as ColorKey) : null;
  const newColor: ColorKey = pickedColor ?? settingColor ?? 'gray';
  const [busy, setBusy] = React.useState(false);
  // True only while an Add is under way, so the Add button spins for its own add and not for a rename or a delete.
  const [adding, setAdding] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // Every change here is kept at once; "Saved" beside the heading says so.
  const { saved, flash } = useSavedTicks();
  // A stage of the gardener's own, while "Delete it?" is being asked.
  const [deleting, setDeleting] = React.useState<{ id: number; name: string } | null>(null);

  const { data: stages = [], refetch } = useQuery({ queryKey: [STAGES_QUERY_KEY, language], queryFn: () => getStages(language) });

  // Named as the server says them in the app's language (Harvesting, Finishing; the gardener's own where renamed).
  const editable = React.useMemo(() => selectableStages(stages), [stages]);

  /*
   * Dragging a row to a new place, saved when it is let go.
   *
   * The order the gardener sees in every stage picker is this list's, so
   * whoever grows things in an order the catalogue did not anticipate can
   * put their own stages where they belong.
   */
  /**
   * A stage's id, for the drag list to tell its rows apart.
   *
   * @param stage - One of the stages that can be reordered.
   * @returns Its id.
   */
  const stageId = React.useCallback((stage: (typeof editable)[number]) => stage.stage_id, []);
  const reorder = useDragReorder({
    items: editable,
    idOf: stageId,
    disabled: busy,
    onCommit: async (ids) => {
      // A refused save has been reported on screen; the hook only needs to
      // know to put the rows back.
      if (!(await run(() => reorderStages(ids)))) throw new Error('not saved');
    },
  });

  /** Runs one change, reads the stages again, and keeps a failure on screen. */
  const run = async (work: () => Promise<void>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await refetch();
      // Photo pickers and the cards read stages from this same key, and the
      // cards paint themselves from the colours it carries.
      await queryClient.invalidateQueries({ queryKey: [STAGES_QUERY_KEY] });
      await queryClient.invalidateQueries({ queryKey: [PLANTS_QUERY_KEY] });
      flash('stages');
      return true;
    } catch (err) {
      setError(messageFor(err, t));
      return false;
    } finally {
      setBusy(false);
    }
  };

  /** Adds a stage of the gardener's own by the typed name and chosen colour. */
  const handleAdd = async (): Promise<void> => {
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    try {
      await run(async () => {
        await createStage(name, pickedColor ?? settingColor ?? undefined);
        setNewName('');
      });
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <p className="flex items-center gap-2 text-base font-medium">
          {t('profile.labels.stages.title')}
          <SavedTick show={saved('stages')} />
        </p>
        <p className="text-sm text-muted-foreground">
          {t('profile.labels.stages.description')}
        </p>
      </div>

      <ul className="space-y-2">
        {reorder.ordered.map((stage) => {
          const theme = themeForColorKey(stage.color_key);
          const hidden = Boolean(stage.is_hidden);
          const lifted = reorder.dragging === stage.stage_id;
          return (
            <li
              key={stage.stage_id}
              {...reorder.rowProps(stage.stage_id)}
              className={cn(
                'flex items-center gap-2 rounded-sm transition-shadow',
                hidden && 'opacity-60',
                lifted && 'bg-muted shadow-md ring-1 ring-border',
              )}
            >
              <button
                type="button"
                {...reorder.handleProps(stage.stage_id)}
                disabled={busy}
                aria-label={t('profile.labels.reorder', { name: stage.name })}
                className="-my-1 flex h-9 w-9 cursor-grab items-center justify-center rounded-sm text-muted-foreground hover:text-foreground active:cursor-grabbing disabled:opacity-40"
              >
                <GripVertical className="h-4 w-4" aria-hidden="true" />
              </button>
              <ColorPicker
                value={stage.color_key ?? 'green'}
                describedThing={stage.name}
                onChange={(colorKey) => run(() => updateStage(stage.stage_id, { colorKey }))}
              />
              <Input
                defaultValue={stage.name}
                maxLength={limits.STAGE_NAME_MAX}
                disabled={busy}
                aria-label={t('profile.labels.stages.nameFor', { name: stage.name })}
                // Kept on Enter, as a list's names are, or on leaving the box.
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                onBlur={(e) => {
                  const name = e.target.value.trim();
                  if (!name || name === stage.name) {
                    e.target.value = stage.name;
                    return;
                  }
                  // Refused, the box goes back to the name that is saved; the
                  // reason is said under the list. It used to keep the refused
                  // words, which read as though they had been kept.
                  const box = e.currentTarget;
                  void run(() => updateStage(stage.stage_id, { name })).then((ok) => { if (!ok) box.value = stage.name; });
                }}
                className="h-8 max-w-[16rem]"
              />
              <Pill size="sm" theme={theme} className="hidden shrink-0 sm:inline-flex">{stage.name}</Pill>

              <div className="ml-auto flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => run(() => updateStage(stage.stage_id, { isHidden: !hidden }))}
                  disabled={busy}
                  aria-label={t(
                    hidden ? 'profile.labels.stages.show' : 'profile.labels.stages.hide',
                    { name: stage.name }
                  )}
                  className="rounded-sm p-1 text-muted-foreground hover:text-foreground disabled:opacity-40"
                >
                  {hidden
                    ? <EyeOff className="h-4 w-4" aria-hidden="true" />
                    : <Eye className="h-4 w-4" aria-hidden="true" />}
                </button>

                {/* Only a gardener's own stage can go. The rest hide, because
                    the app's stage rules look them up. */}
                {stage.is_own ? (
                  <button
                    type="button"
                    onClick={() => setDeleting({ id: stage.stage_id, name: stage.name })}
                    disabled={busy}
                    aria-label={t('profile.labels.stages.remove', { name: stage.name })}
                    className="rounded-sm p-1 text-muted-foreground hover:text-destructive disabled:opacity-40"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                ) : (
                  <span className="w-6" aria-hidden="true" />
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <div className="flex items-center gap-2">
        <ColorPicker
          value={newColor}
          describedThing={t('profile.labels.stages.newThing')}
          onChange={setNewColor}
        />
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value.slice(0, limits.STAGE_NAME_MAX))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); void handleAdd(); }
          }}
          maxLength={limits.STAGE_NAME_MAX}
          disabled={busy}
          placeholder={t('profile.labels.stages.placeholder')}
          aria-label={t('profile.labels.stages.placeholder')}
          className="h-8 max-w-[16rem]"
        />
        <Button onClick={handleAdd} busy="adding" loading={adding} disabled={busy || newName.trim().length === 0} size="sm">
          <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
          {t('sitewide.add')}
        </Button>
      </div>

      {error && (
        <p className="flex items-center gap-1.5 text-sm text-destructive">
          <X className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span><FailureText text={error} /></span>
        </p>
      )}

      {/* Asked first: it used to go at the touch of the bin. */}
      <BottomPanel
        open={deleting !== null}
        onOpenChange={(next) => { if (!next && !busy) setDeleting(null); }}
        title={t('profile.labels.deleteTitle', { name: deleting?.name ?? '' })}
      >
        <p className="text-sm text-muted-foreground">{t('profile.labels.stages.deleteBody')}</p>
        {error && <p role="alert" className="text-sm text-destructive"><FailureText text={error} /></p>}
        <ActionRow cancelLabel={t('profile.labels.keep')} disabled={busy} onCancel={() => setDeleting(null)}>
          <Button
            variant="destructive"
            busy="deleting"
            loading={busy}
            onClick={async () => {
              if (!deleting) return;
              const id = deleting.id;
              // The question stays open until the server has answered: it closes on success, and on a failure it stays
              // with the failure words (run has already reported the failure).
              if (await run(() => deleteStage(id))) setDeleting(null);
            }}
          >
            {t('profile.labels.delete')}
          </Button>
        </ActionRow>
      </BottomPanel>
    </div>
  );
};

/**
 * Turns the server's refusals into something a gardener can act on.
 *
 * @param err - Whatever the change threw.
 * @param t - The translator.
 * @returns A sentence for under the list, translated.
 */
function messageFor(
  err: unknown,
  t: (key: string, vars?: Record<string, string | number>) => string
): string {
  const data = (err as { response?: { data?: { code?: string; inUse?: number } } })
    ?.response?.data;
  if (data?.code === 'STAGE_IN_USE') {
    return t('profile.labels.stages.inUse', { count: data.inUse ?? 0 });
  }
  if (data?.code === 'STAGE_NOT_YOURS') return t('profile.labels.stages.notYours');
  // The server's own refusal where it gave one; never axios's "Request
  // failed with status code 500".
  return errorMessage(err, t('profile.labels.saveFailed'));
}
